import { useEffect, useMemo, useRef, useState } from 'react'
import { store } from 'store/index'
import { buildPeriod } from './analyticsDates'

// Филиал и период общие для всех экранов аналитики: выбрал «Чиланзар, 30 дней» на одном
// отчёте — перешёл на другой и видишь то же самое. Хранится на вкладку браузера.
// pos_ids — набор филиалов сетевых отчётов (переносится только между экранами, где он есть).
const SHARED_STORAGE_KEY = 'mison_analytics_filters'
const SHARED_KEYS = ['pos_id', 'pos_ids', 'period', 'start_date', 'end_date']

// Чей фильтр: вкладка переживает выход и вход в другой логин, а филиал предыдущего логина
// подставляться не должен (дашборд открывался с чужой точкой)
export function accountStorageKey() {
	const account = store.getState().account || {}
	return `${account.login || localStorage.getItem('login') || ''}|${account.id ?? ''}`
}

function readShared() {
	try {
		const stored = JSON.parse(sessionStorage.getItem(SHARED_STORAGE_KEY)) || {}
		return stored.owner === accountStorageKey() ? stored : {}
	} catch (e) {
		return {}
	}
}

function writeShared(filter) {
	try {
		const shared = {}
		SHARED_KEYS.forEach(key => {
			if (key in filter) shared[key] = filter[key]
		})
		sessionStorage.setItem(SHARED_STORAGE_KEY, JSON.stringify({ ...readShared(), ...shared, owner: accountStorageKey() }))
	} catch (e) { }
}

function withFreshPeriod(filter) {
	// Сохранённый пресет пересчитываем от сегодняшней даты, свой период оставляем как есть
	if (filter.period && filter.period !== 'custom') return { ...filter, ...buildPeriod(filter.period) }
	return filter
}

function sameFilter(a, b) {
	return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Состояние фильтра экрана аналитики.
 *
 * draft — то, что пользователь накликал; applied — то, по чему построен отчёт.
 * Изменения копятся в draft и уходят на сервер только по «Применить»: тяжёлые отчёты
 * не дёргаются на каждый клик в мультиселекте.
 *
 * defaults — полный набор полей экрана. Для периода кладите { ...buildPeriod('7days') }.
 * load(filter) — запрос отчёта, вызывается при открытии и при каждом применении.
 */
function useAnalyticsFilters(defaults, load, { shared = true } = {}) {
	const defaultsRef = useRef(defaults)
	const loadRef = useRef(load)
	loadRef.current = load

	const initial = useMemo(() => {
		let filter = withFreshPeriod(defaults)
		if (shared) {
			const stored = readShared()
			const patch = {}
			SHARED_KEYS.forEach(key => {
				if (key in defaults && key in stored && stored[key] !== undefined) patch[key] = stored[key]
			})
			// Период переносим, только если экран работает с периодом и пресет ему известен
			if (!('period' in defaults)) {
				delete patch.period
				delete patch.start_date
				delete patch.end_date
			}
			filter = withFreshPeriod({ ...filter, ...patch })
		}
		return filter
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const [draft, setDraft] = useState(initial)
	const [applied, setApplied] = useState(initial)

	function apply(next = draft) {
		setDraft(next)
		setApplied(next)
		if (shared) writeShared(next)
		return loadRef.current(next)
	}

	function patch(values) {
		setDraft(prev => ({ ...prev, ...values }))
	}

	// Для переключателей внутри отчёта (вкладка, группировка) — применяются сразу,
	// не трогая неприменённые правки в панели фильтров
	function patchApplied(values) {
		const next = { ...applied, ...values }
		setApplied(next)
		setDraft(prev => ({ ...prev, ...values }))
		return loadRef.current(next)
	}

	function reset() {
		return apply(withFreshPeriod(defaultsRef.current))
	}

	useEffect(() => {
		loadRef.current(initial)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const isDirty = !sameFilter(draft, applied)
	const isDefault = sameFilter(withFreshPeriod(draft), withFreshPeriod(defaultsRef.current))

	return { draft, applied, patch, apply, patchApplied, reset, isDirty, isDefault }
}

export default useAnalyticsFilters
